import type {
  Balance,
  BillingSummary,
  ConfirmSetupIntentRequest,
  CreatePaymentIntentRequest,
  CreatePaymentIntentResult,
  FinalizeRequest,
  FinalizeResult,
  Purchase,
  ReplaceCardResult,
  SetupIntentResult,
} from "./model"
import { baseApi } from "@/base"
import type { Page } from "@/base"
import type { Uuid } from "@/utils"

const api = baseApi
  // Assignments/MyProfiles are declared by the assignment feature on the same
  // baseApi; re-declaring is idempotent and lets finalize invalidate them so
  // the lists refresh the moment a queued send/self-assignment runs.
  .enhanceEndpoints({
    addTagTypes: ["Billing", "Purchases", "Assignments", "MyProfiles"],
  })
  .injectEndpoints({
    endpoints: (build) => ({
      getBillingSummary: build.query<BillingSummary, void>({
        query: () => "billing/summary",
        providesTags: ["Billing"],
      }),
      getBalance: build.query<Balance, void>({
        query: () => "billing/balance",
        providesTags: ["Billing"],
      }),
      createPaymentIntent: build.mutation<
        CreatePaymentIntentResult,
        CreatePaymentIntentRequest
      >({
        // No invalidation: credits land on finalize / the webhook, not here.
        query: (body) => ({
          url: "billing/payment-intent",
          method: "POST",
          body,
        }),
      }),
      finalizeTopUp: build.mutation<FinalizeResult, FinalizeRequest>({
        query: (body) => ({ url: "billing/finalize", method: "POST", body }),
        invalidatesTags: ["Billing", "Purchases", "Assignments", "MyProfiles"],
      }),
      createSetupIntent: build.mutation<SetupIntentResult, void>({
        query: () => ({ url: "billing/setup-intent", method: "POST" }),
      }),
      replacePaymentMethod: build.mutation<
        ReplaceCardResult,
        ConfirmSetupIntentRequest
      >({
        query: (body) => ({
          url: "billing/payment-method",
          method: "PUT",
          body,
        }),
        invalidatesTags: ["Billing"],
      }),
      getPurchases: build.query<Page<Purchase>, void>({
        // First page (backend defaults to 20/page); pagination UI is post-v1.
        query: () => "billing/purchases",
        providesTags: ["Purchases"],
      }),
      // Fetch the issued invoice PDF as a Blob (the auth + Tenant-Slug headers
      // ride the shared baseQuery, which a plain <a download> can't carry). The
      // store ignores serializability for the "api" cache, so a Blob is fine.
      downloadInvoice: build.query<Blob, Uuid>({
        query: (id) => ({
          url: `billing/invoices/${id}`,
          responseHandler: (response) => response.blob(),
        }),
      }),
    }),
  })

export const {
  useGetBillingSummaryQuery,
  useGetBalanceQuery,
  useCreatePaymentIntentMutation,
  useFinalizeTopUpMutation,
  useCreateSetupIntentMutation,
  useReplacePaymentMethodMutation,
  useGetPurchasesQuery,
  useLazyDownloadInvoiceQuery,
} = api

export default api
